import { PARKING_PROPERTIES_FILTERS, ParkTimeContractType } from '@repo/domain';
import type {
  FlatPrice,
  ParkingPropertyFilters,
  ParkingPropertyFilter,
  ParkTimeContract,
  GeoBounds,
  GeoCoordinates,
} from '@repo/domain';
import { isApplicationMode } from '@repo/env-variables';
import { createStore, persist } from '@repo/zustand';

import { parkingFilterDictionary, type ParkingFilterTopKey } from '@shared/constants/parkingFilterDictionary';
import type { ParkingSortingOptionId } from '@shared/constants/parkingSortingOptions';
import { calculateParkDurationFromContract, DEFAULT_PARK_DURATION } from '@shared/utils/timeContractCalculations';

export interface LastPriceEstimate {
  parkingId: string;
  flatPrice: FlatPrice;
}

interface State {
  // Filters
  filters: ParkingPropertyFilters;
  setFilter: (filter: ParkingPropertyFilter, value: boolean) => void;
  toggleFilter: (filter: ParkingPropertyFilter) => void;
  resetFilters: () => void;
  areFiltersOpen: boolean;
  toggleFilters: () => void;
  closeFilters: () => void;

  // Sorting.
  sorting: ParkingSortingOptionId;
  setSorting: (sorting: ParkingSortingOptionId) => void;

  // Parking definition.
  selectedParking: {
    id?: string;
    slug?: string;
    spotId?: string;
    spotName?: string;
  };
  selectionNonce: number;
  setSelectedParking: (id?: string, slug?: string) => void;
  setSelectedSpot: (spotId?: string, spotName?: string) => void;
  // The price rule (package) the user chose to buy directly. Its presence makes
  // the next reservation a package purchase rather than a standard one; the
  // reservation flow consumes and clears it on create.
  selectedPriceRuleId: string | undefined;
  setSelectedPriceRuleId: (priceRuleId: string | undefined) => void;
  // The last estimated price the user saw when starting a standard reservation;
  // the summary compares it with the reservation's recalculated price to tell
  // the user the price changed. Absent when the flow did not start from a shown estimate.
  lastPriceEstimate: LastPriceEstimate | undefined;
  setLastPriceEstimate: (lastPriceEstimate: LastPriceEstimate | undefined) => void;
  parkTimeContract: ParkTimeContract;
  setParkStart: (parkStart: Date | undefined) => void;
  setParkDuration: (parkDuration: number) => void;
  setParkUntil: (parkTimeContract: ParkTimeContract) => void;

  // Duration dialog.
  isTimeContractPickerModalOpen: boolean;
  setIsTimeContractPickerModalOpen: (open: boolean) => void;

  // Park start drawer.
  isParkStartDrawerOpen: boolean;
  setIsParkStartDrawerOpen: (open: boolean) => void;

  searchedAddress: { label: string; location: GeoCoordinates | undefined };
  setSearchedAddress: (label: string, location?: GeoCoordinates) => void;

  // Map camera (source of truth for location-based search).
  // mapBounds only updates when center or zoom changes — viewport-resize-only
  // bounds shifts are intentionally ignored to avoid redundant searches.
  mapBounds: GeoBounds | undefined;
  mapCenter: GeoCoordinates | undefined;
  mapZoom: number | undefined;
  setMapCamera: (camera: { center: GeoCoordinates; zoom: number; bounds: GeoBounds }) => void;
  // Bumped whenever the user manually pans or zooms the map; the map drawer
  // listens to this to collapse itself (instead of collapsing on outside click).
  mapMoveNonce: number;
  bumpMapMoveNonce: () => void;

  // Reset.
  resetSearch: () => void;
  // Clears the selection left over from a finished reservation flow (whether it
  // succeeded or failed), so the next attempt starts clean rather than being
  // pinned to a now-stale spot. Currently only the spot; widen as needed.
  resetAfterReservation: () => void;
}

const createFilterDefaults = (): ParkingPropertyFilters =>
  Object.fromEntries(PARKING_PROPERTIES_FILTERS.map((key) => [key, false] as const)) as ParkingPropertyFilters;

const createDefaults = (): Pick<
  State,
  | 'filters'
  | 'areFiltersOpen'
  | 'sorting'
  | 'selectedParking'
  | 'selectionNonce'
  | 'selectedPriceRuleId'
  | 'lastPriceEstimate'
  | 'parkTimeContract'
  | 'isTimeContractPickerModalOpen'
  | 'isParkStartDrawerOpen'
  | 'searchedAddress'
  | 'mapBounds'
  | 'mapCenter'
  | 'mapZoom'
  | 'mapMoveNonce'
> => ({
  filters: createFilterDefaults(),
  areFiltersOpen: false,
  sorting: 'nearest',
  selectedParking: { id: undefined, slug: undefined, spotId: undefined, spotName: undefined },
  selectionNonce: 0,
  selectedPriceRuleId: undefined,
  lastPriceEstimate: undefined,
  parkTimeContract: { type: ParkTimeContractType.FixedDuration, duration: DEFAULT_PARK_DURATION },
  isTimeContractPickerModalOpen: false,
  isParkStartDrawerOpen: false,
  searchedAddress: { label: '', location: undefined },
  mapBounds: undefined,
  mapCenter: undefined,
  mapZoom: undefined,
  mapMoveNonce: 0,
});

export const useParkingSearchStore = createStore<State>({
  name: 'ParkingSearchStore',
  isDev: isApplicationMode('dev'),
})(
  persist(
    (set) => ({
      // Default state.
      ...createDefaults(),

      // Filters
      setFilter: (filter: ParkingPropertyFilter, value: boolean) => {
        set((state) => ({
          filters: {
            ...state.filters,
            [filter]: value,
          },
        }));
      },
      toggleFilter: (filter: ParkingPropertyFilter) => {
        set((state) => ({
          filters: {
            ...state.filters,
            [filter]: !state.filters[filter],
          },
        }));
      },
      resetFilters: () => {
        set({ filters: createFilterDefaults() });
      },
      toggleFilters: () => set((state) => ({ areFiltersOpen: !state.areFiltersOpen })),
      closeFilters: () => set({ areFiltersOpen: false }),

      // Sorting.
      setSorting: (sorting: ParkingSortingOptionId) => {
        set(() => ({ sorting }));
      },

      // Parking definition.
      setSelectedParking: (id?: string, slug?: string) => {
        set((state) => {
          // Selecting a different parking clears any previously-chosen spot; re-selecting the
          // same parking preserves it (so a deep-linked/scanned spot survives map re-selection).
          const parkingChanged = state.selectedParking.id !== id;
          return {
            selectedParking: {
              id,
              slug,
              spotId: parkingChanged ? undefined : state.selectedParking.spotId,
              spotName: parkingChanged ? undefined : state.selectedParking.spotName,
            },
            selectionNonce: state.selectionNonce + 1,
          };
        });
      },
      setSelectedSpot: (spotId?: string, spotName?: string) => {
        set((state) => ({ selectedParking: { ...state.selectedParking, spotId, spotName } }));
      },
      setSelectedPriceRuleId: (priceRuleId: string | undefined) => {
        set({ selectedPriceRuleId: priceRuleId });
      },
      setLastPriceEstimate: (lastPriceEstimate: LastPriceEstimate | undefined) => {
        set({ lastPriceEstimate });
      },
      setParkStart(parkStart: Date | undefined): void {
        set((state) => ({
          parkTimeContract: { ...state.parkTimeContract, startAtTimestamp: parkStart?.getTime() },
        }));
      },
      setParkDuration(parkDuration: number): void {
        set((state) => ({
          parkTimeContract: {
            type: ParkTimeContractType.FixedDuration,
            duration: parkDuration,
            startAtTimestamp: state.parkTimeContract.startAtTimestamp,
          },
        }));
      },
      setParkUntil(parkTimeContract: ParkTimeContract): void {
        set((state) => ({
          parkTimeContract: { ...parkTimeContract, startAtTimestamp: state.parkTimeContract.startAtTimestamp },
        }));
      },

      setSearchedAddress: (label: string, location?: GeoCoordinates) => set({ searchedAddress: { label, location } }),

      // Duration dialog.
      setIsTimeContractPickerModalOpen: (open: boolean) => set({ isTimeContractPickerModalOpen: open }),

      // Park start drawer.
      setIsParkStartDrawerOpen: (open: boolean) => set({ isParkStartDrawerOpen: open }),

      // Map camera. Commits new bounds only when the center moved or the zoom
      // level changed; pure viewport-resize events leave state untouched.
      setMapCamera({ center, zoom, bounds }): void {
        set((state) => {
          const centerUnchanged =
            state.mapCenter !== undefined &&
            Math.abs(state.mapCenter.latitude - center.latitude) < 1e-9 &&
            Math.abs(state.mapCenter.longitude - center.longitude) < 1e-9;
          const zoomUnchanged = state.mapZoom === zoom;
          if (centerUnchanged && zoomUnchanged) return state;
          return { mapCenter: center, mapZoom: zoom, mapBounds: bounds };
        });
      },
      bumpMapMoveNonce: () => set((state) => ({ mapMoveNonce: state.mapMoveNonce + 1 })),

      // Reset.
      resetSearch: () => {
        set(createDefaults());
      },
      resetAfterReservation: () => {
        set((state) => ({
          selectedParking: { ...state.selectedParking, spotId: undefined, spotName: undefined },
          selectedPriceRuleId: undefined,
          lastPriceEstimate: undefined,
        }));
      },
    }),
    { name: 'parking-search-storage-v17' }
  )
);

export const selectParkStartAtTimestamp = (state: State): number | undefined => state.parkTimeContract.startAtTimestamp;

export const selectSelectedFiltersCount = (state: State) =>
  (Object.keys(parkingFilterDictionary) as ParkingFilterTopKey[]).reduce((count, key) => {
    if (key === 'covered' || key === 'evCharging' || key === 'secured' || key === 'size') {
      const subKeys = Object.keys(parkingFilterDictionary[key].subProperties) as ParkingPropertyFilter[];
      return count + (subKeys.some((subKey) => state.filters[subKey]) ? 1 : 0);
    }
    return count + (state.filters[key] ? 1 : 0);
  }, 0);

export const selectParkDuration = (state: State): number => calculateParkDurationFromContract(state.parkTimeContract);
